import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useFourWeekPPCData } from '@/hooks/useFourWeekPPCData';
import PPCLineChart from '@/components/pmt3/charts/PPCLineChart';

// Chart Loading Skeleton Component
const ChartSkeleton: React.FC<{ title: string }> = React.memo(({ title }) => (
  <Card className="w-full">
    <CardHeader className="pb-4">
      <CardTitle className="text-lg font-semibold text-center">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        {/* Legend skeleton */}
        <div className="flex justify-center gap-6">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>
        
        {/* Chart area skeleton */}
        <div className="relative">
          <Skeleton className="h-80 w-full" />
          
          {/* Simulate chart lines */}
          <div className="absolute inset-4 flex items-end justify-between">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <Skeleton className="h-16 w-2" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
));

ChartSkeleton.displayName = 'ChartSkeleton';

// Main Dashboard Component
const FourWeekPPCChartsDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = useFourWeekPPCData(
    selectedProduct?.companyName,
    selectedProduct?.id
  );

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Dashboard Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          4-Week PPC Charts
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Performance trends over the last 4 weeks
        </p>
      </div>

      {/* Error State */}
      {error && (
        <Card className="w-full">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-red-600 dark:text-red-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Error Loading Charts
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {error.message}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-8">
          <ChartSkeleton title="Spend & Sales" />
          <ChartSkeleton title="CPC & ACoS" />
        </div>
      )}

      {/* Charts */}
      {!isLoading && !error && data && (
        <div className="space-y-8">
          {/* Spend & Sales Chart */}
          <Card className="w-full shadow-lg">
            <CardContent className="p-6">
              <PPCLineChart
                data={data.spendSalesData}
                title="Spend & Sales"
                leftMetric={{
                  key: 'spend',
                  name: 'Spend',
                  color: '#3B82F6', // Blue
                  gradientId: 'spendGradient',
                  type: 'dollar'
                }}
                rightMetric={{
                  key: 'sales',
                  name: 'Sales',
                  color: '#EF4444', // Red
                  gradientId: 'salesGradient',
                  type: 'dollar'
                }}
                height={350}
              />
            </CardContent>
          </Card>

          {/* CPC & ACoS Chart */}
          <Card className="w-full shadow-lg">
            <CardContent className="p-6">
              <PPCLineChart
                data={data.cpcAcosData}
                title="CPC & ACoS"
                leftMetric={{
                  key: 'cpc',
                  name: 'CPC',
                  color: '#3B82F6', // Blue
                  gradientId: 'cpcGradient',
                  type: 'dollarCents'
                }}
                rightMetric={{
                  key: 'acos',
                  name: 'ACoS',
                  color: '#EF4444', // Red
                  gradientId: 'acosGradient',
                  type: 'percentage'
                }}
                height={350}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Data State */}
      {!isLoading && !error && !data && (
        <Card className="w-full">
          <CardContent className="p-12">
            <div className="text-center">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                No Chart Data Available
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                {selectedProduct 
                  ? 'No 4-week PPC data found for this product. Data may not have been synced yet.'
                  : 'Please select a product to view 4-week PPC charts.'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FourWeekPPCChartsDashboard;